import axios from "axios";

const API = process.env.REACT_APP_API_URL;

// Intercept 401 responses from any admin API call.
// Clears the stale token and redirects to login so the user is never
// left on a broken dashboard. The login endpoint is excluded so a
// wrong-password 401 does not trigger an unwanted redirect.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const isLoginRequest = error.config?.url?.includes("/admin/login");
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem("adminToken");
      window.location.href = "/admin/login";
    }
    return Promise.reject(error);
  }
);

const getAuthHeaders = () => ({
  headers: { "x-admin-token": localStorage.getItem("adminToken") || "" }
});

export const adminLogin = async (email, password) => {
  const res = await axios.post(`${API}/admin/login`, { email, password });
  return res.data;
};

export const adminLogout = () =>
  axios.post(`${API}/admin/logout`);

export const fetchContacts = (page = 1) =>
  axios.get(`${API}/admin/contacts?page=${page}`, getAuthHeaders());

export const deleteContact = (id) =>
  axios.delete(`${API}/admin/contacts/${id}`, getAuthHeaders());

export const fetchBusinesses = (page = 1) =>
  axios.get(`${API}/admin/businesses?page=${page}`, getAuthHeaders());

export const deleteBusiness = (id) =>
  axios.delete(`${API}/admin/businesses/${id}`, getAuthHeaders());

export const updateBusiness = (id, data) =>
  axios.put(`${API}/admin/businesses/${id}`, data, getAuthHeaders());

export const updateVerification = (id, verificationStatus) =>
  axios.put(`${API}/admin/businesses/${id}/verify`, { verificationStatus }, getAuthHeaders());

export const getStats = async () => {
  const res = await axios.get(`${API}/admin/stats`, getAuthHeaders());
  return res.data;
};

export const fetchAdminCategories = () =>
  axios.get(`${API}/admin/categories`, getAuthHeaders());

export const createAdminCategory = (name) =>
  axios.post(`${API}/admin/categories`, { name }, getAuthHeaders());

export const updateAdminCategory = (id, data) =>
  axios.put(`${API}/admin/categories/${id}`, data, getAuthHeaders());

export const deleteAdminCategory = (id) =>
  axios.delete(`${API}/admin/categories/${id}`, getAuthHeaders());

export const getAdminMobileVerificationSettings = () =>
  axios.get(`${API}/settings/mobile-verification`, getAuthHeaders());

export const updateAdminMobileVerificationSettings = (data) =>
  axios.put(`${API}/admin/settings/mobile-verification`, data, getAuthHeaders());

export const getSubscriptionSettings = () =>
  axios.get(`${API}/admin/settings/subscription`, getAuthHeaders());

export const updateSubscriptionSettings = (data) =>
  axios.put(`${API}/admin/settings/subscription`, data, getAuthHeaders());

export const fetchAdminCities = () =>
  axios.get(`${API}/admin/cities`, getAuthHeaders());

export const createAdminCity = (data) =>
  axios.post(`${API}/admin/cities`, data, getAuthHeaders());

export const updateAdminCity = (id, data) =>
  axios.put(`${API}/admin/cities/${id}`, data, getAuthHeaders());

export const deleteAdminCity = (id) =>
  axios.delete(`${API}/admin/cities/${id}`, getAuthHeaders());

// Swaps sortOrder between two adjacent categories.
// catA and catB are category objects with { _id, sortOrder }.
// Uses two sequential calls to the existing PUT endpoint.
export const swapCategorySortOrder = async (catA, catB) => {
  await updateAdminCategory(catA._id, { sortOrder: catB.sortOrder });
  await updateAdminCategory(catB._id, { sortOrder: catA.sortOrder });
};
